import { appVariant } from '@util/AppUtils';

type DefaultMutedAccent = 'default' | 'muted' | 'accent';
type DefaultMutedAccentOnAccent = DefaultMutedAccent | 'onAccent';

type SurfaceColor = DefaultMutedAccentOnAccent;
type ActionColor = DefaultMutedAccent;
type ContentColor = DefaultMutedAccentOnAccent;
type DecorativeColor = DefaultMutedAccentOnAccent;
type FlowColor = DefaultMutedAccentOnAccent | 'disabled';
type StateColor = 'error' | 'success' | 'successMuted' | 'select';
type SubscriptionColor = 'addon' | 'surface' | 'accent';

type ColorType = string;
type Colors<ColorKeys extends string> = Record<ColorKeys, ColorType>;

type ColorMap = {
  surface: Colors<SurfaceColor>;
  action: Colors<ActionColor>;
  content: Colors<ContentColor>;
  decorative: Colors<DecorativeColor>;
  state: Colors<StateColor>;
  flow: Colors<FlowColor>;
  transparent: ColorType;
  shadow: ColorType;
  subscription: Colors<SubscriptionColor>;
  flareup: ColorType;
  star: ColorType;
  gradient: ColorType;
};

export const Color: ColorMap = {
  surface: {
    default: appVariant({
      nerva: 'url(/images/nerva-background.svg) center/cover no-repeat, #0A1412',
      meno: '#FFFEFB',
      quit: '#FFFFFF',
      pain: '#FFFFFF',
    }),
    muted: appVariant({
      nerva: 'rgba(0, 0, 0, 0.2)',
      meno: '#F6F1EC',
      quit: '#F9F6F3',
      pain: '#F7F8FA',
    }),
    accent: appVariant({
      nerva: 'rgba(0, 0, 0, 0.2)',
      meno: '#DBE8F4',
      quit: '#ABDCD7',
      pain: '#EDF0F3',
    }),
    onAccent: appVariant({
      nerva: 'rgba(255, 255, 255, 0.1)',
      meno: '#ECF3FC',
      quit: '#EBFDFB',
      pain: '#E6FAFF',
    }),
  },
  action: {
    default: appVariant({
      nerva: '#367B6E',
      meno: '#56799A',
      quit: '#FF9548',
      pain: '#0093D6',
    }),
    muted: appVariant({
      nerva: '#F1F3F4',
      meno: '#8EA8C1',
      quit: '#F4FAF9',
      pain: '#EAF2F6',
    }),
    accent: appVariant({
      nerva: '#DDE2E9',
      meno: '#DBE8F4',
      quit: '#FF9548',
      pain: '#C2E5ED',
    }),
  },
  content: {
    default: appVariant({
      nerva: '#FFFFFF',
      meno: '#000',
      quit: '#000',
      pain: '#000',
    }),
    muted: appVariant({
      nerva: 'rgba(255, 255, 255, 0.8)',
      meno: '#79625C',
      quit: '#878787',
      pain: '#5F5F5F',
    }),
    accent: appVariant({
      nerva: '#3E9374',
      meno: '#5588B5',
      quit: '#2D6660',
      pain: '#0083C6',
    }),
    onAccent: appVariant({
      nerva: '#6FB1A0',
      meno: '#425E78',
      quit: '#16423D',
      pain: '#0093D6',
    }),
  },
  decorative: {
    default: appVariant({
      nerva: 'rgba(255, 255, 255, 0.2)',
      meno: '#F0E9E2',
      quit: '#E0E0E0',
      pain: '#E0E0E0',
    }),
    muted: appVariant({
      nerva: 'rgba(255, 255, 255, 0.2)',
      meno: '#F5F5F5',
      quit: '#F5F5F5',
      pain: '#E7E7E7',
    }),
    accent: appVariant({
      nerva: '#000000',
      meno: '#8EA8C1',
      quit: '#FF9548',
      pain: '#F1F6FF',
    }),
    onAccent: appVariant({
      nerva: '#FFFFFF',
      meno: '#425E78',
      quit: '#2D6660',
      pain: '#0093D6',
    }),
  },
  flow: {
    default: appVariant({
      nerva: '#FFFFFF',
      meno: '#79625C',
      quit: '#000',
      pain: '#3A3F45',
    }),
    muted: appVariant({
      nerva: 'rgba(255, 255, 255, 0.15)',
      meno: '#F0E9E2',
      quit: '#ABDCD7',
      pain: '#DDE2E9',
    }),
    accent: appVariant({
      nerva: '#FFFFFF',
      meno: '#425E78',
      quit: '#FF9548',
      pain: '#0093D6',
    }),
    onAccent: appVariant({
      nerva: '#EDF0F3',
      meno: '#4E6E89',
      quit: '#2D6660',
      pain: '#C2E5ED',
    }),
    disabled: appVariant({
      nerva: 'rgba(255, 255, 255, 0.3)',
      meno: '#8EA8C1',
      quit: '#FFDFC8',
      pain: '#E6FAFF',
    }),
  },
  state: {
    success: '#54B35E',
    successMuted: '#E8F3EE',
    error: '#FB3F3B',
    select: appVariant({
      nerva: '#3A3F45',
      meno: '#79625C',
      quit: '#FFFFFF',
      pain: '#3A3F45',
    }),
  },
  subscription: {
    surface: appVariant({
      nerva: '#DDECE5',
      meno: '#E8ECEF',
      quit: '#E0F4F2',
      pain: '#D2ECF8',
    }),
    addon: appVariant({
      nerva: '#FFDF92',
      meno: '#FFBA88',
      quit: '#ABDCD7',
      pain: '#FFDF92',
    }),
    accent: '#FFFFFF',
  },
  transparent: 'transparent',
  shadow: '#000',
  flareup: '#95B7FF',
  star: '#E0B51A',
  gradient: appVariant({
    nerva: 'url(/images/nerva-background.svg) center/cover no-repeat, #0A1412',
    meno: 'linear-gradient(#FCF9F6, #DBE8F4);',
    quit: 'linear-gradient(#FFF, #E0F5F3);',
    pain: 'linear-gradient(#FFFFFF, #D2ECF8);',
  }),
};
